import api from './api';
import { Customer } from './customers';
import { Business } from './businesses';
import { TaskType } from './taskTypes';
import { TaskTemplate } from './taskTemplates';
import { Quotation } from './quotations';
import { Team } from './teams';
import { Staff } from './staffs';

export interface User {
  id: number;
  name: string;
  email: string;
}

export interface SubTaskTemplate {
  id: number;
  name: string;
  task_template_id?: number;
  enabled: boolean;
  list_type?: 'general' | 'check_in' | 'check_out' | string;
  taskTemplate?: TaskTemplate;
}

export interface TaskSubTask {
  id?: number | string; // Allow string for temporary items
  task_id: number;
  sub_task_template_id: number;
  name?: string; // Custom name, if not provided, use subTaskTemplate.name
  completed: boolean;
  created_by?: number;
  updated_by?: number;
  created_at?: string;
  updated_at?: string;
  subTaskTemplate?: SubTaskTemplate;
  creator?: User;
  updater?: User;
}

export interface TaskDraftReport {
  id: number;
  task_id: number;
  attachment?: string;
  batch_no?: number;
  approval_status?: 'pending' | 'approved' | 'rejected';
  enabled: boolean;
  created_by?: number;
  created_at: string;
  creator?: User;
}

export interface TaskComment {
  id: number;
  task_id: number;
  user_id?: number;
  comment?: string;
  created_by?: number;
  created_at: string;
  user?: User;
  creator?: User;
}

export interface TaskReview {
  id: number;
  task_id: number;
  customer_id?: number;
  review?: string;
  rating?: number;
  created_by?: number;
  created_at: string;
  customer?: Customer;
  creator?: User;
}

export interface TaskTimelog {
  id: number;
  task_id: number;
  date: string;
  hour: number | string;
  description?: string;
  enabled: boolean;
  created_at: string;
}

export interface TaskStatusLog {
  id: number;
  portal: 'admin' | 'customer';
  module: string;
  reference_id: number;
  from_status: string | null;
  to_status: string;
  updated_by?: number | null;
  updated_at: string;
  created_at: string;
  updater?: User;
}

export interface TaskReferenceItem {
  id: number;
  reference_number?: string;
  referenceNumber?: string;
}

export interface Task {
  id: number;
  type?: 'personal' | 'business';
  reference_id?: number;
  reference_number?: string;
  customer_id?: number;
  business_id?: number;
  task_type_id?: number;
  year?: string;
  task_template_id?: number;
  created_date?: string;
  due_date?: string;
  deadline?: string;
  plan_date?: string;
  plan_hour?: number;
  actual_hour?: number;
  quotation_id?: number;
  team_id?: number;
  staff_id?: number;
  status?: 'Plan' | 'Progress' | 'Review' | 'Approve' | 'Completed';
  is_sent?: boolean;
  is_completed?: boolean;
  enabled: boolean;
  created_by?: number;
  updated_by?: number;
  created_at: string;
  updated_at: string;
  customer?: Customer;
  business?: Business;
  taskType?: TaskType;
  taskTemplate?: TaskTemplate;
  task_type?: TaskType;
  task_template?: TaskTemplate;
  quotation?: Quotation;
  team?: Team;
  staff?: Staff;
  subTasks?: TaskSubTask[];
  draftReports?: TaskDraftReport[];
  comments?: TaskComment[];
  reviews?: TaskReview[];
  timelogs?: TaskTimelog[];
  status_logs?: TaskStatusLog[];
  statusLogs?: TaskStatusLog[];
  letters?: TaskReferenceItem[];
  dispatches?: TaskReferenceItem[];
}

export interface CreateTaskData {
  type?: 'personal' | 'business';
  reference_id?: number;
  customer_id?: number;
  business_id?: number;
  task_type_id?: number;
  year?: number;
  task_template_id?: number;
  created_date?: string;
  due_date?: string;
  deadline?: string;
  plan_date?: string;
  plan_hour?: number;
  actual_hour?: number;
  quotation_id?: number;
  team_id?: number;
  staff_id?: number;
  status?: 'Plan' | 'Progress' | 'Review' | 'Approve' | 'Completed';
  is_sent?: boolean;
  is_completed?: boolean;
  enabled?: boolean;
  sub_tasks?: Array<{
    sub_task_template_id: number;
    completed?: boolean;
  }>;
}

export interface CreateTaskDraftReportData {
  task_id: number;
  attachment?: File;
  enabled?: boolean;
  batch_no?: number;
  approval_status?: 'pending' | 'approved' | 'rejected';
}

export interface CreateTaskCommentData {
  task_id: number;
  comment: string;
}

export interface CreateTaskReviewData {
  task_id: number;
  customer_id?: number;
  review: string;
  rating?: number;
}

export interface CreateTaskTimelogData {
  task_id: number;
  date: string;
  hour: number | string;
  description?: string;
  enabled?: boolean;
}

const unwrap = <T>(response: any): T => response.data?.data ?? response.data;

export const taskService = {
  async getAll(params?: { page?: number; per_page?: number }) {
    const response = await api.get('/tasks', { params });
    return response.data;
  },

  async getById(id: number): Promise<Task> {
    const response = await api.get(`/tasks/${id}`);
    return unwrap<Task>(response);
  },

  async create(data: CreateTaskData): Promise<Task> {
    const response = await api.post('/tasks', data);
    return unwrap<Task>(response);
  },

  async update(id: number, data: Partial<CreateTaskData>): Promise<Task> {
    const response = await api.put(`/tasks/${id}`, data);
    return unwrap<Task>(response);
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/tasks/${id}`);
  },
  async approve(id: number): Promise<Task> {
    const response = await api.post(`/tasks/${id}/approve`);
    return unwrap<Task>(response);
  },
  async import(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/tasks/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  async downloadImportTemplate() {
    const response = await api.get('/tasks/import/template', {
      responseType: 'blob',
    });
    return response.data as Blob;
  },
  async export(payload: { ids?: number[]; select_all?: boolean; excluded_ids?: number[]; filters?: Record<string, any> }) {
    const response = await api.post(
      '/tasks/export',
      payload,
      { responseType: 'blob' }
    );
    return response.data as Blob;
  },
  async batchDelete(payload: { ids?: number[]; select_all?: boolean; excluded_ids?: number[]; filters?: Record<string, any> }) {
    const response = await api.post('/tasks/batch-delete', payload);
    return response.data;
  },
};

export const taskDraftReportService = {
  async create(data: CreateTaskDraftReportData): Promise<TaskDraftReport> {
    const formData = new FormData();
    formData.append('task_id', data.task_id.toString());
    if (data.attachment) {
      formData.append('attachment', data.attachment);
    }
    if (typeof data.batch_no === 'number') {
      formData.append('batch_no', data.batch_no.toString());
    }
    if (data.approval_status) {
      formData.append('approval_status', data.approval_status);
    }
    formData.append('enabled', (data.enabled ?? true).toString());
    
    const response = await api.post('/task-draft-reports', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return unwrap<TaskDraftReport>(response);
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/task-draft-reports/${id}`);
  },
};

export const taskCommentService = {
  async create(data: CreateTaskCommentData): Promise<TaskComment> {
    const response = await api.post('/task-comments', data);
    return unwrap<TaskComment>(response);
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/task-comments/${id}`);
  },
};

export const taskReviewService = {
  async create(data: CreateTaskReviewData): Promise<TaskReview> {
    const response = await api.post('/task-reviews', data);
    return unwrap<TaskReview>(response);
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/task-reviews/${id}`);
  },
};

export const taskTimelogService = {
  async getAll(): Promise<{ data: TaskTimelog[] }> {
    const response = await api.get('/task-timelogs');
    return response.data;
  },

  async create(data: CreateTaskTimelogData): Promise<TaskTimelog> {
    const response = await api.post('/task-timelogs', data);
    return unwrap<TaskTimelog>(response);
  },

  async update(id: number, data: Partial<CreateTaskTimelogData>): Promise<TaskTimelog> {
    const response = await api.put(`/task-timelogs/${id}`, data);
    return unwrap<TaskTimelog>(response);
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/task-timelogs/${id}`);
  },
};
